'use client'

// Shared live preview. The card portion is the REAL search-grid ListingCard
// (user decision 2026-07-15: "the listing template example should match our
// card style 1 to 1") rendered from a Listing synthesized out of the draft;
// story + contact keep the prototype's skeleton-shimmer detail sections
// (post.jsx:74-188). The card is display-only here (pointer-events-none) —
// its buttons would act on a listing that doesn't exist yet.
import * as React from 'react'
import { Clock, Eye, TrendingUp } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Chip } from '@/components/roostr'
import { ListingCard } from '@/components/listings'
import type { ListingCardStats } from '@/components/listings/listing-card'
import { useI18n } from '@/lib/i18n'
import { MIcon } from './icons'
import { PRICE_COMPS } from './post-data'
import type { PostDraft } from './post-data'
import type { Listing } from '@/lib/types'

// The card's micro-vizzes (price position bar, area footprint, floor gauge)
// compare against a pool — on the search page that's the visible results.
// The preview stands the draft among the price comparables plus a plausible
// Vinohrady spread, so the FULL search-card anatomy renders (user requirement
// 2026-07-15: the preview must match the listing card 1:1).
const COMP_PRICES = PRICE_COMPS.buckets.flatMap((count, i) =>
  Array.from(
    { length: count },
    () => PRICE_COMPS.bucketStart + i * PRICE_COMPS.bucketStep + PRICE_COMPS.bucketStep / 2
  )
)
export const PREVIEW_STATS: ListingCardStats = {
  prices: COMP_PRICES,
  areas: [38, 45, 52, 58, 61, 66, 74, 81],
  floors: [0, 1, 2, 3, 4, 5, 6],
  priceMin: Math.min(...COMP_PRICES),
  priceMax: Math.max(...COMP_PRICES),
  priceMedian: COMP_PRICES.slice().sort((a, b) => a - b)[Math.floor(COMP_PRICES.length / 2)],
}

function Skel({
  w = '100%',
  h = 12,
  r = 7,
  style,
}: {
  w?: number | string
  h?: number
  r?: number
  style?: React.CSSProperties
}) {
  return <span className="shimmer block" style={{ width: w, height: h, borderRadius: r, ...style }} />
}

function PvSection({
  filled,
  children,
  skeleton,
  label,
}: {
  filled: boolean
  children: React.ReactNode
  skeleton: React.ReactNode
  label?: string
}) {
  return (
    <div className={cn(filled && 'pv-flash', 'rounded-[10px]')}>
      {filled ? (
        children
      ) : (
        <div>
          {skeleton}
          {label ? (
            <div className="mt-[5px] font-mono text-[9.5px] font-bold tracking-[0.6px] text-ink-muted">{label}</div>
          ) : null}
        </div>
      )}
    </div>
  )
}

// The draft, wearing the search-card's data shape. Photos are the mock
// gradients (no URLs) — RealPhotos falls back to its palette blocks while
// keeping the real card's arrows/dots/counter chrome.
export function draftToListing(draft: PostDraft): Listing {
  const f = draft.facts
  // Extra fees fold into the card's price_breakdown the way scraped listings
  // carry them — so the effective price and the ⓘ popover match search 1:1.
  const extras = draft.extras || []
  const sumBy = (re: RegExp) => extras.filter((e) => re.test(e.label)).reduce((a, e) => a + (e.amount || 0), 0)
  const parking = sumBy(/park/i)
  const internet = sumBy(/internet/i)
  const other = extras.reduce((a, e) => a + (e.amount || 0), 0) - parking - internet
  return {
    id: 'post-draft-preview',
    is_active: 1,
    title: draft.title || '',
    price: draft.price,
    price_currency: 'CZK',
    rooms: f?.rooms,
    area: f?.area,
    floor: f?.floor,
    total_floors: f?.totalFloors,
    city: draft.address?.city || 'Praha',
    district: draft.address?.district,
    street: draft.address?.street,
    keywords: f?.features ?? [],
    price_breakdown: {
      utilities_monthly: draft.utilities || undefined,
      parking_monthly: parking || undefined,
      internet_monthly: internet || undefined,
      service_charges_monthly: other || undefined,
      deposit_amount: draft.deposit || undefined,
    },
    updates: undefined,
    images: (draft.photos || []).map(() => ({ url: '' })),
    image_tags: [],
    original_url: '',
    marketplace: 'Roostr',
    relevance_score: 0,
  }
}

export interface PreviewPanelProps {
  draft: PostDraft
  quality: number
}

export function PreviewPanel({ draft, quality }: PreviewPanelProps) {
  const { t } = useI18n()
  const f = draft.facts
  const kept = draft.photos || []
  // The real card appears as soon as there's anything to show; before that
  // the skeleton teaches what will land where.
  const cardReady = kept.length > 0 || !!f || !!draft.title || !!draft.address
  return (
    <div className="overflow-hidden rounded-shape-lg border border-outline-variant bg-surface shadow-e1">
      <div className="flex items-center gap-[10px] border-b border-outline-variant px-4 py-[11px]">
        <Eye size={16} className="text-ink-muted" />
        <span className="text-[12px] font-bold tracking-[0.4px] text-ink-secondary">
          {t('post.previewHeader', 'WHAT SEEKERS WILL SEE')}
        </span>
        <span className="flex-1" />
        <span className={cn('font-mono text-[11px] font-bold', quality >= 80 ? 'text-success' : 'text-ink-muted')}>
          {quality}%
        </span>
        <span className="h-1 w-[54px] overflow-hidden rounded-[2px] bg-surface-high">
          <span
            className={cn(
              'block h-full rounded-[2px] transition-[width] duration-[600ms] ease-emphasized',
              quality >= 80 ? 'bg-success' : 'bg-primary'
            )}
            style={{ width: quality + '%' }}
          />
        </span>
      </div>
      {/* The seller's #1 reason to post here — direct listings outrank the
          scraped ones in search. Loud on purpose (user request 2026-07-15). */}
      <div className="flex items-center gap-2.5 border-b border-outline-variant bg-primary-container px-4 py-[10px]">
        <TrendingUp size={17} className="shrink-0 text-on-primary-container" />
        <span className="text-[12.5px] font-semibold leading-[1.45] text-on-primary-container">
          {t(
            'post.roostrBoost',
            'Listings posted on Roostr rank above imported ones — seekers see yours first.'
          )}
        </span>
      </div>
      <div className="flex flex-col gap-[13px] px-4 pb-4 pt-[14px]">
        {/* ── The search card, 1:1 ── */}
        <div className="mb-[2px] font-mono text-[9.5px] font-bold tracking-[0.6px] text-ink-muted">
          {t('post.previewLabelCard', 'YOUR CARD IN SEARCH RESULTS')}
        </div>
        {cardReady ? (
          <div className="pv-flash pointer-events-none">
            <ListingCard listing={draftToListing(draft)} stats={PREVIEW_STATS} />
          </div>
        ) : (
          <div>
            <div className="shimmer flex items-center justify-center" style={{ aspectRatio: '16 / 10', borderRadius: 12 }}>
              <span className="font-mono text-[10.5px] font-bold tracking-[0.6px] text-ink-muted">
                {t('post.previewPhotosPlaceholder', 'YOUR PHOTOS LAND HERE')}
              </span>
            </div>
            <Skel w="72%" h={18} style={{ marginTop: 12 }} />
            <Skel w="45%" h={12} style={{ marginTop: 8 }} />
            <Skel w="38%" h={22} style={{ marginTop: 10 }} />
            <div className="mt-3 flex gap-2">
              <Skel h={62} r={10} />
              <Skel h={62} r={10} />
              <Skel h={62} r={10} />
            </div>
          </div>
        )}
        {/* ── Detail-page extras ── */}
        <PvSection
          filled={!!draft.body}
          skeleton={
            <>
              <Skel h={11} />
              <Skel h={11} w="94%" style={{ marginTop: 6 }} />
              <Skel h={11} w="60%" style={{ marginTop: 6 }} />
            </>
          }
          label={t('post.previewLabelStory', 'STORY — SHOWN ON THE DETAIL PAGE')}
        >
          <p className="text-[12.5px] leading-[1.6] text-ink-secondary">{draft.body}</p>
        </PvSection>
        {draft.contact ? (
          <div>
            <div className="mb-[6px] font-mono text-[9.5px] font-bold tracking-[0.6px] text-ink-muted">
              {t('post.previewLabelContact', 'CONTACT')}
            </div>
            <div className="flex flex-wrap gap-[5px]">
              {draft.contact.email ? <Chip small icon={<MIcon name="mail" size={13} />} label={draft.contact.email} /> : null}
              {draft.contact.phone ? <Chip small icon={<MIcon name="call" size={13} />} label={draft.contact.phone} /> : null}
              {draft.contact.telegram ? (
                <Chip small icon={<MIcon name="send" size={13} />} label={draft.contact.telegram} />
              ) : null}
              {draft.contact.instagram ? (
                <Chip small icon={<MIcon name="photo_camera" size={13} />} label={draft.contact.instagram} />
              ) : null}
            </div>
            {draft.contact.schedule ? (
              <div className="mt-[5px] flex items-center gap-1 text-[11px] text-ink-muted">
                <Clock size={12} />
                {t('post.previewContactsVisible', 'Contacts visible')} {draft.contact.schedule.days.join(' · ')} —{' '}
                {draft.contact.schedule.from}:00–{draft.contact.schedule.to}:00
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  )
}
